import React, { useState } from "react";
import {
  Box,
  Button,
  Drawer,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { CustomInput } from "global/components";
import { signerFieldValidationPatterns } from "global/constants/ConstantObject";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import {
  setSelectedField,
  updateFieldInSignRequest,
  updateFieldOfSelectedFile,
} from "redux/appSlice";
import { FieldListItem, FileField } from "models/interfaces";
import strings from "global/constants/StringConstants";
import {
  FIELD_LENGTH_LIMIT_MESSAGE,
  MAX_FIELD_LENGTH,
  PATTERN_FIELD_TYPES,
  blockNonLengthKeys,
  getNextRadioGroupId,
  toLengthDigits,
} from "../../SignRequestHelper";
import { pressableProps } from "../mobileA11y";
import moment from "moment";

const DATE_FORMAT_OPTIONS = [
  "MM/DD/YYYY",
  "DD/MM/YYYY",
  "YYYY-MM-DD",
  "DD-MM-YYYY",
  "MM-DD-YYYY",
  "DD MMM YYYY",
  "MMM DD, YYYY",
];
const NEW_RADIO_GROUP_VALUE = "__new_radio_group__";
const CUSTOM_PATTERN_VALUE = "__custom__";
// Types that share the desktop required/read-only (+ placeholder/max) block.
const SHARED_TYPES = [
  strings.text,
  strings.email,
  strings.name,
  strings.companyName,
  strings.title,
  strings.dateSigned,
  strings.checkbox,
  strings.dropDown,
];

/**
 * "Field Data" properties sheet (Figma node 1346-7902 / content 1346-7903).
 * Renders the field-data controls PER FIELD TYPE and applies them LIVE, exactly
 * like the desktop PropertiesDrawer.getFieldDataAccordionDetails:
 *   - all types: Label, Tooltip
 *   - radio_button: Group, Required, Read Only
 *   - date: Date Format, Required, Read Only
 *   - text/email/name/company/title/dateSigned/checkbox/dropdown: Required,
 *     Read Only, (text: Multiline), Placeholder + Max Length (except email;
 *     dropdown has no Max Length)
 *   - drop_down: Options
 *   - library-placed text/dropdown: Default Value
 *   - Validation: library text/dropdown, or a required Fields-panel text field
 * Uses the desktop `CustomInput` / `CustomSwitch` components.
 */
const MobileFieldDataSheet = ({
  open,
  onClose,
  field,
}: {
  open: boolean;
  onClose: () => void;
  field: FileField | null;
}) => {
  const theme = useTheme();
  const m = theme.palette.mobile;
  const dispatch = useAppDispatch();
  const { selectedFile, signRequest } = useAppSelector(
    (s: RootState) => s.app,
  );
  const [maxLengthError, setMaxLengthError] = useState("");

  const t = field?.type || "";
  const isRadio = t === strings.radioButton;
  const isDate = t === strings.date;
  const isEmail = t === strings.email;
  const isText = t === strings.text;
  const inShared = SHARED_TYPES.includes(t);
  const hasRequiredReadOnly = inShared || isRadio || isDate;
  const isDropDown = t === strings.dropDown;
  const hasPlaceholder = inShared && !isEmail;
  const hasMaxLength = hasPlaceholder && !isDropDown;
  // Only a field placed from the Custom Fields library gets the custom-field
  // options (default value, min length, custom pattern) -- desktop parity.
  const isLibraryPlacedField = !!field?.customFieldId;
  // "I'm the only signer": the sender fills the field, so it holds the value.
  const isSelfSignRequest = signRequest?.selfSigned === true;
  const defaultKey = isSelfSignRequest ? "value" : "defaultValue";
  const defaultBound = String(
    (isSelfSignRequest ? field?.value : field?.defaultValue) || "",
  );
  const showValidation = isLibraryPlacedField
    ? PATTERN_FIELD_TYPES.includes(t)
    : isText && !!field?.required;

  // Live field update (desktop parity): file + sign request + selected field,
  // propagating required/readOnly across a radio group.
  const applyField = (name: string, value: any, propagateRadio = false) => {
    if (!field) return;
    const patch: any = { id: field.id, [name]: value };
    // The default and the value on the document are one value for a library
    // field; the server only copies the default into an empty value.
    if (name === "defaultValue" && isLibraryPlacedField) {
      patch.value = value;
    }
    dispatch(updateFieldOfSelectedFile(patch));
    dispatch(updateFieldInSignRequest(patch));
    dispatch(setSelectedField({ ...field, ...patch }));
    if (propagateRadio && isRadio && field.groupId) {
      (selectedFile?.fields || []).forEach((f: FileField) => {
        if (f.groupId === field.groupId && f.id !== field.id) {
          const up: any = { id: f.id, [name]: value };
          dispatch(updateFieldOfSelectedFile(up));
          dispatch(updateFieldInSignRequest(up));
        }
      });
    }
  };

  // Same rules as the desktop Properties panel's Max Length.
  const onMaxLength = (e: any) => {
    const digits = toLengthDigits(e.target.value);
    if (digits === "") {
      setMaxLengthError("Max length is required.");
      // Stored as 0 so the controlled input can actually render empty; returning without it
      // snapped the old number straight back under the "required" message.
      applyField("maxLength", 0);
      return;
    }
    const numericValue = Number(digits);
    if (numericValue <= 0) {
      setMaxLengthError("Please enter a valid max length.");
      return;
    }
    const currentValueLength = String(field?.value || "").length;
    // Kept as typed (not clamped); the editor's step validation stops it from being sent.
    setMaxLengthError(
      numericValue > MAX_FIELD_LENGTH
        ? FIELD_LENGTH_LIMIT_MESSAGE
        : numericValue < currentValueLength
          ? `Max length cannot be less than current content length (${currentValueLength}).`
          : "",
    );
    applyField("maxLength", numericValue);
  };

  const getRadioGroupOptions = (): { id: string; label: string }[] => {
    const seen = new Map<string, number>();
    let order = 0;
    (selectedFile?.fields || []).forEach((f: FileField) => {
      if (f.type === strings.radioButton && f.groupId && !seen.has(f.groupId)) {
        seen.set(f.groupId, order++);
      }
    });
    const letterFor = (idx: number) => {
      let s = "";
      let n = idx;
      do {
        s = String.fromCharCode(65 + (n % 26)) + s;
        n = Math.floor(n / 26) - 1;
      } while (n >= 0);
      return s;
    };
    return Array.from(seen.entries()).map(([id, idx]) => ({
      id,
      label: `Group ${letterFor(idx)}`,
    }));
  };

  const onGroupChange = (val: string) => {
    if (!field) return;
    const groupId =
      val === NEW_RADIO_GROUP_VALUE ? getNextRadioGroupId(selectedFile) : val;
    if (groupId === field.groupId) return;
    const patch: any = { id: field.id, groupId, value: "false" };
    dispatch(updateFieldOfSelectedFile(patch));
    dispatch(updateFieldInSignRequest(patch));
    dispatch(setSelectedField({ ...field, groupId, value: "false" }));
  };

  const onDateFormat = (newFormat: string) => {
    if (!field || !newFormat || newFormat === field.dateFormat) return;
    const oldFormat = field.dateFormat || "MM/DD/YYYY";
    const updates: any = { id: field.id, dateFormat: newFormat };
    if (field.value) {
      const parsed = moment(field.value, oldFormat, true);
      if (parsed.isValid()) updates.value = parsed.format(newFormat);
    }
    dispatch(updateFieldOfSelectedFile(updates));
    dispatch(updateFieldInSignRequest(updates));
    dispatch(setSelectedField({ ...field, ...updates }));
  };

  // ---- dropdown options (same rules as desktop PropertiesDrawer) ----------

  const dropdownOptions: FieldListItem[] = field?.listItems ?? [];

  const commitDropdownOptions = (listItems: FieldListItem[]) => {
    if (!field) return;
    const updates: Partial<FileField> = { id: field.id, listItems };
    // Keep the default in step with the options it must belong to.
    if (
      field.defaultValue &&
      !listItems.some((item) => item.text === field.defaultValue)
    ) {
      updates.defaultValue = "";
    }
    dispatch(updateFieldOfSelectedFile(updates));
    dispatch(updateFieldInSignRequest(updates));
    dispatch(setSelectedField({ ...field, ...updates }));
  };

  const addDropdownOption = () =>
    commitDropdownOptions([
      ...dropdownOptions,
      {
        id: `option_${Date.now()}`,
        text: `Option ${dropdownOptions.length + 1}`,
      },
    ]);

  const renameDropdownOption = (index: number, text: string) =>
    commitDropdownOptions(
      dropdownOptions.map((item, i) => (i === index ? { ...item, text } : item)),
    );

  const removeDropdownOption = (index: number) =>
    commitDropdownOptions(dropdownOptions.filter((_, i) => i !== index));

  const duplicateOptionTexts = new Set(
    dropdownOptions
      .map((item) => item.text.trim())
      .filter((text, i, all) => text !== "" && all.indexOf(text) !== i),
  );

  // ---- validation ---------------------------------------------------------

  /**
   * A named pattern is looked up by display name on the server; a custom one
   * is used verbatim, so `customValidation` tells the two apart.
   */
  const onValidationTypeChange = (value: string) => {
    if (!field) return;
    const updates: Partial<FileField> =
      value === CUSTOM_PATTERN_VALUE
        ? { id: field.id, customValidation: true, validationPattern: "" }
        : {
            id: field.id,
            customValidation: false,
            validationPattern: value,
            ...(value === "none" ? { validationMessage: "" } : {}),
          };
    dispatch(updateFieldOfSelectedFile(updates));
    dispatch(updateFieldInSignRequest(updates));
    dispatch(setSelectedField({ ...field, ...updates }));
  };

  const onMinLength = (e: any) => {
    applyField("minLength", Number(toLengthDigits(e.target.value)) || 0);
  };

  const customPatternError = (() => {
    if (!field?.customValidation) return "";
    const pattern = field.validationPattern || "";
    if (pattern.trim() === "") return "Enter a regular expression.";
    try {
      new RegExp(pattern);
      return "";
    } catch {
      return "This is not a valid regular expression.";
    }
  })();

  const minLengthError =
    (field?.minLength ?? 0) > MAX_FIELD_LENGTH
      ? FIELD_LENGTH_LIMIT_MESSAGE
      : (field?.minLength ?? 0) > 0 &&
          (field?.maxLength ?? 0) > 0 &&
          (field?.minLength ?? 0) > (field?.maxLength ?? 0)
        ? "Minimum length cannot be greater than max length."
        : "";

  const handleClear = () => {
    if (!field) return;
    applyField("placeholder", "");
    applyField("toolTip", "");
    applyField("maxLength", 200);
    setMaxLengthError("");
  };

  const sectionLabel = {
    fontSize: "0.75rem",
    fontWeight: 500,
    color: m.grey[400],
    textTransform: "uppercase",
    mb: 0.5,
  } as const;

  const selectSx = {
    width: "100%",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: m.base[900],
    borderRadius: "8px",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: m.grey[50] },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: m.grey[50] },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: m.ePurple[500],
    },
    "& .MuiSelect-select": { padding: "14px 16px" },
  } as const;

  const switchRow = (
    labelText: string,
    name: string,
    checked: boolean,
    propagate = false,
  ) => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      key={name}
    >
      <Typography
        component="label"
        htmlFor={`mobile-field-${name}-switch`}
        sx={{ fontSize: "0.875rem", fontWeight: 500, color: m.base[900] }}
      >
        {labelText}
      </Typography>
      <Switch
        id={`mobile-field-${name}-switch`}
        checked={!!checked}
        onChange={(_, val) => applyField(name, val, propagate)}
        inputProps={{ "aria-label": labelText }}
      />
    </Stack>
  );

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { px: 2, pb: 3, borderTopLeftRadius: 0, borderTopRightRadius: 0 },
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": "field-data-sheet-title",
      }}
    >
      {/* Drag handle */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 1.5 }}>
        <Box
          aria-hidden="true"
          sx={{ width: 42, height: 4, borderRadius: 2, bgcolor: m.grey[50] }}
        />
      </Box>

      {/* Title + Clear */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography
          id="field-data-sheet-title"
          component="h2"
          sx={{ fontSize: "1rem", fontWeight: 600, color: m.base[900] }}
        >
          Field Data
        </Typography>
        <Typography
          {...pressableProps(handleClear, { label: "Clear field data" })}
          sx={{
            fontSize: "0.875rem",
            fontWeight: 700,
            color: m.grey[400],
            cursor: "pointer",
          }}
        >
          Clear
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          mb: 2.5,
          maxHeight: "55vh",
          overflowY: "auto",
        }}
      >
        {/* Label (all types) */}
        <CustomInput
          size="small"
          id="mobile-field-label-input"
          name="label"
          value={field?.label || ""}
          placeHolder="Enter field label"
          onChange={(e: any) => applyField("label", e.target.value)}
          propsToInputElement={{ "aria-label": "Field label" }}
        />

        {/* Radio group */}
        {isRadio && (
          <Box>
            <Typography id="mobile-field-group-label" sx={sectionLabel}>
              Group
            </Typography>
            <Select
              size="small"
              labelId="mobile-field-group-label"
              value={field?.groupId || ""}
              onChange={(e) => onGroupChange(e.target.value as string)}
              sx={selectSx}
            >
              {getRadioGroupOptions().map((opt) => (
                <MenuItem key={opt.id} value={opt.id} sx={{ fontSize: "0.875rem" }}>
                  {opt.label}
                </MenuItem>
              ))}
              <MenuItem
                value={NEW_RADIO_GROUP_VALUE}
                sx={{ fontSize: "0.875rem", color: m.ePurple[500], fontWeight: 600 }}
              >
                + New group
              </MenuItem>
            </Select>
          </Box>
        )}

        {/* Date format */}
        {isDate && (
          <Box>
            <Typography id="mobile-field-dateformat-label" sx={sectionLabel}>
              Date Format
            </Typography>
            <Select
              size="small"
              labelId="mobile-field-dateformat-label"
              value={field?.dateFormat || "MM/DD/YYYY"}
              onChange={(e) => onDateFormat(e.target.value as string)}
              sx={selectSx}
            >
              {DATE_FORMAT_OPTIONS.map((fmt) => (
                <MenuItem key={fmt} value={fmt} sx={{ fontSize: "0.875rem" }}>
                  {fmt}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        {/* Dropdown options */}
        {isDropDown && (
          <Box>
            <Typography sx={sectionLabel}>Options</Typography>
            <Stack spacing={1}>
              {dropdownOptions.map((item, index) => (
                <Stack
                  key={item.id}
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <CustomInput
                      size="small"
                      id={`mobile-field-option-${index}-input`}
                      value={item.text}
                      placeHolder={`Option ${index + 1}`}
                      onChange={(e: any) =>
                        renameDropdownOption(index, e.target.value)
                      }
                      propsToInputElement={{
                        "aria-label": `Option ${index + 1}`,
                        ...(duplicateOptionTexts.has(item.text.trim()) ||
                        item.text.trim() === ""
                          ? { "aria-invalid": true }
                          : {}),
                      }}
                    />
                  </Box>
                  <IconButton
                    aria-label={`Remove option ${index + 1}`}
                    disabled={dropdownOptions.length <= 1}
                    onClick={() => removeDropdownOption(index)}
                    sx={{ color: m.grey[400], width: 44, height: 44 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            {duplicateOptionTexts.size > 0 && (
              <FormHelperText error role="alert">
                Options must be unique.
              </FormHelperText>
            )}
            {dropdownOptions.some((item) => item.text.trim() === "") && (
              <FormHelperText error role="alert">
                Every option needs a label.
              </FormHelperText>
            )}
            <Typography
              {...pressableProps(addDropdownOption, { label: "Add option" })}
              sx={{
                mt: 1,
                py: 1,
                display: "inline-block",
                fontSize: "0.875rem",
                fontWeight: 600,
                color: m.ePurple[500],
                cursor: "pointer",
              }}
            >
              + Add option
            </Typography>
          </Box>
        )}

        {/* Required / Read Only / Multiline */}
        {hasRequiredReadOnly && (
          <Stack spacing={0.5}>
            {switchRow("Required", "required", !!field?.required, true)}
            {switchRow("Read Only", "readOnly", !!field?.readOnly, true)}
            {isText && switchRow("Multiline", "multiLine", !!field?.multiLine)}
          </Stack>
        )}

        {/* Default value (library-placed text / dropdown) */}
        {isLibraryPlacedField && (isText || isDropDown) && (
          <Box>
            <Typography id="mobile-field-default-label" sx={sectionLabel}>
              {isSelfSignRequest ? "Value" : "Default Value"}
            </Typography>
            {isDropDown ? (
              <Select
                size="small"
                labelId="mobile-field-default-label"
                displayEmpty
                value={defaultBound}
                onChange={(e) => applyField(defaultKey, e.target.value)}
                sx={selectSx}
              >
                <MenuItem value="" sx={{ fontSize: "0.875rem" }}>
                  No default
                </MenuItem>
                {dropdownOptions
                  .filter((item) => item.text.trim() !== "")
                  .map((item) => (
                    <MenuItem
                      key={item.id}
                      value={item.text}
                      sx={{ fontSize: "0.875rem" }}
                    >
                      {item.text}
                    </MenuItem>
                  ))}
              </Select>
            ) : (
              <CustomInput
                size="small"
                id="mobile-field-default-input"
                value={defaultBound}
                placeHolder={
                  isSelfSignRequest
                    ? "Enter the value"
                    : "Pre-filled for the signer"
                }
                onChange={(e: any) => applyField(defaultKey, e.target.value)}
                propsToInputElement={{
                  "aria-label": isSelfSignRequest ? "Value" : "Default value",
                }}
              />
            )}
          </Box>
        )}

        {/* Placeholder + Max Length */}
        {hasPlaceholder && (
          <>
            <CustomInput
              size="small"
              id="mobile-field-placeholder-input"
              name="placeholder"
              value={field?.placeholder || ""}
              placeHolder="Enter placeholder text"
              onChange={(e: any) => applyField("placeholder", e.target.value)}
              propsToInputElement={{ "aria-label": "Placeholder text" }}
            />
            {hasMaxLength && (
              <CustomInput
                size="small"
                type="number"
                id="mobile-field-maxlength-input"
                name="maxLength"
                value={field?.maxLength || ""}
                placeHolder="Max length"
                onChange={onMaxLength}
                onKeyDown={blockNonLengthKeys}
                error={maxLengthError || null}
                propsToInputElement={{
                  "aria-label": "Maximum length",
                  min: 1,
                  max: MAX_FIELD_LENGTH,
                  step: 1,
                }}
              />
            )}
          </>
        )}

        {/* Validation (the desktop "Validations" accordion) */}
        {showValidation && isText && (
          <Box>
            <Typography id="mobile-field-validation-label" sx={sectionLabel}>
              Validation Type
            </Typography>
            <Select
              size="small"
              labelId="mobile-field-validation-label"
              value={
                field?.customValidation
                  ? CUSTOM_PATTERN_VALUE
                  : field?.validationPattern || "none"
              }
              onChange={(e) => onValidationTypeChange(e.target.value)}
              sx={selectSx}
            >
              {Object.keys(signerFieldValidationPatterns).map((pattern) => (
                <MenuItem
                  key={pattern}
                  value={pattern}
                  sx={{ fontSize: "0.875rem" }}
                >
                  {pattern}
                </MenuItem>
              ))}
              {isLibraryPlacedField && (
                <MenuItem
                  value={CUSTOM_PATTERN_VALUE}
                  sx={{ fontSize: "0.875rem" }}
                >
                  Custom pattern
                </MenuItem>
              )}
            </Select>

            {field?.customValidation && (
              <Box sx={{ mt: 1.5 }}>
                <CustomInput
                  size="small"
                  id="mobile-field-pattern-input"
                  value={field?.validationPattern || ""}
                  placeHolder="Pattern, e.g. ^[A-Z]{2}-[0-9]{4}$"
                  onChange={(e: any) =>
                    applyField("validationPattern", e.target.value)
                  }
                  propsToInputElement={{
                    "aria-label": "Validation pattern",
                    ...(customPatternError
                      ? {
                          "aria-invalid": true,
                          "aria-describedby": "mobile-field-pattern-error",
                        }
                      : {}),
                  }}
                />
                {customPatternError && (
                  <FormHelperText
                    error
                    id="mobile-field-pattern-error"
                    role="alert"
                  >
                    {customPatternError}
                  </FormHelperText>
                )}
              </Box>
            )}

            {(field?.customValidation ||
              (field?.validationPattern &&
                field.validationPattern !== "none")) && (
              <Box sx={{ mt: 1.5 }}>
                <CustomInput
                  size="small"
                  id="mobile-field-validation-message-input"
                  value={field?.validationMessage || ""}
                  placeHolder="Validation message"
                  onChange={(e: any) =>
                    applyField("validationMessage", e.target.value)
                  }
                  propsToInputElement={{ "aria-label": "Validation message" }}
                />
              </Box>
            )}

            {/* Text only: a dropdown's value is always one of its options. */}
            {isLibraryPlacedField && (
              <Box sx={{ mt: 1.5 }}>
                <CustomInput
                  size="small"
                  type="number"
                  id="mobile-field-minlength-input"
                  value={field?.minLength || ""}
                  placeHolder="Min length"
                  onChange={onMinLength}
                  onKeyDown={blockNonLengthKeys}
                  propsToInputElement={{
                    "aria-label": "Min length",
                    min: 0,
                    max: MAX_FIELD_LENGTH,
                    step: 1,
                    ...(minLengthError
                      ? {
                          "aria-invalid": true,
                          "aria-describedby": "mobile-field-minlength-error",
                        }
                      : {}),
                  }}
                />
                {minLengthError && (
                  <FormHelperText
                    error
                    id="mobile-field-minlength-error"
                    role="alert"
                  >
                    {minLengthError}
                  </FormHelperText>
                )}
              </Box>
            )}
          </Box>
        )}
        {showValidation && isDropDown && (
          <Typography sx={{ fontSize: "0.8125rem", color: m.grey[400] }}>
            The signer can only pick one of the options listed above.
          </Typography>
        )}

        {/* Tooltip (all types) */}
        <CustomInput
          size="small"
          id="mobile-field-tooltip-input"
          name="toolTip"
          value={field?.toolTip || ""}
          placeHolder="Enter tooltip text"
          onChange={(e: any) => applyField("toolTip", e.target.value)}
          propsToInputElement={{ "aria-label": "Tooltip text" }}
        />
      </Box>

      {/* Update — changes apply live; this dismisses the sheet. */}
      <Button
        fullWidth
        variant="contained"
        onClick={onClose}
        sx={{
          bgcolor: m.ePurple[500],
          color: m.surface.white,
          textTransform: "none",
          fontSize: "0.875rem",
          fontWeight: 700,
          lineHeight: "20px",
          borderRadius: "8px",
          py: 2,
          boxShadow: "none",
          "&:hover": { bgcolor: m.ePurple[700], boxShadow: "none" },
        }}
      >
        Update
      </Button>
    </Drawer>
  );
};

export default MobileFieldDataSheet;
